import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { ORPCError } from '@orpc/client';
import { USER_SESSION_HEARTBEAT_INTERVAL_MS } from '@erp/rpc';
import { rpcClient } from '@/api/rpc/client';
import { isNetworkError } from '@/integrations/sentry/is-network-error';

/** Cap client-side Sentry noise when offline / backend is down. */
const SENTRY_REPORT_COOLDOWN_MS = 5 * 60_000;
let lastSentryReportAt = 0;

function isExpectedHeartbeatFailure(error: unknown): boolean {
  // Logged-out / no-org beats are normal; don't page on them.
  if (
    error instanceof ORPCError &&
    (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN')
  ) {
    return true;
  }
  // Offline, tab navigating away mid-beat, or a transient backend blip surface
  // as a fetch NetworkError. Expected for a best-effort background beat — don't
  // report them.
  const message = error instanceof Error ? error.message : '';
  return isNetworkError(message);
}

/**
 * Sends a periodic activity heartbeat to the backend while the app tab is
 * visible, so we can measure how long people spend in Gently. Best-effort:
 * failures are swallowed and never surface to the user.
 */
export function useSessionHeartbeat() {
  useEffect(() => {
    let inFlight = false;

    async function sendHeartbeat() {
      if (document.visibilityState !== 'visible' || inFlight) {
        return;
      }
      inFlight = true;
      try {
        await rpcClient.userSessions.heartbeat();
      } catch (error) {
        // Tracking must never break the app — drop the beat and try again
        // on the next interval. Unexpected failures go to Sentry (throttled).
        const now = Date.now();
        if (
          !isExpectedHeartbeatFailure(error) &&
          now - lastSentryReportAt >= SENTRY_REPORT_COOLDOWN_MS
        ) {
          lastSentryReportAt = now;
          Sentry.captureException(error, {
            tags: { feature: 'user-session-heartbeat' },
            fingerprint: ['user-session-heartbeat'],
          });
        }
      } finally {
        inFlight = false;
      }
    }

    function handleVisibilityChange() {
      // Fires on both hide and show; sendHeartbeat no-ops when hidden, so
      // this only records the moment the user returns to the tab.
      void sendHeartbeat();
    }

    void sendHeartbeat();
    const intervalId = window.setInterval(
      () => void sendHeartbeat(),
      USER_SESSION_HEARTBEAT_INTERVAL_MS,
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
